import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, CaretRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { listEnrollmentRecords } from "@/lib/enrollment";
import {
  formatChildName,
  formatEnrollmentDate,
  parentStatusLabel,
} from "@/lib/enrollmentTypes";
import StageBadge from "@/components/enrollment/StageBadge";

export default function ParentEnrollment() {
  const { profile } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!profile?.id) return;
    (async () => {
      setLoading(true);
      try {
        const list = await listEnrollmentRecords({
          role: "parent",
          profileId: profile.id,
        });
        setRows(list);
      } catch (err) {
        toast.error(err.message || "Could not load enrollment records");
      } finally {
        setLoading(false);
      }
    })();
  }, [profile?.id]);

  return (
    <div data-testid="parent-enrollment" className="space-y-5">
      <header className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
            Enrollment
          </p>
          <h1 className="mt-1 font-heading text-3xl font-semibold tracking-tight text-neutral-900 sm:text-4xl">
            Your applications
          </h1>
        </div>
        <Link
          to="/parent/enrollment/new"
          data-testid="parent-new-inquiry-button"
          className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-primary-hover active:scale-[0.98]"
        >
          <Plus size={14} weight="bold" />
          New inquiry
        </Link>
      </header>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="h-8 w-8 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
        </div>
      ) : rows.length === 0 ? (
        <div
          data-testid="parent-enrollment-empty"
          className="rounded-2xl border border-dashed border-neutral-200 bg-white p-10 text-center"
        >
          <p className="font-heading text-base font-semibold text-neutral-900">
            No applications yet
          </p>
          <p className="mx-auto mt-2 max-w-sm text-sm text-neutral-500">
            Start a new inquiry to tell us about your child and the kind of
            program you're looking for.
          </p>
        </div>
      ) : (
        <ul className="space-y-2" data-testid="parent-enrollment-list">
          {rows.map((r) => (
            <li key={r.id}>
              <Link
                to={`/parent/enrollment/${r.id}`}
                data-testid={`parent-enr-row-${r.id}`}
                data-stage={r.stage}
                className="flex items-start gap-3 rounded-2xl border border-neutral-200 bg-white p-4 transition-all hover:-translate-y-[1px] hover:shadow-sm"
              >
                <div className="min-w-0 flex-1">
                  <p className="font-heading text-base font-semibold tracking-tight text-neutral-900">
                    {formatChildName(r)}
                  </p>
                  <p className="mt-0.5 text-sm text-neutral-600">
                    {parentStatusLabel(r.stage)}
                  </p>
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <StageBadge stage={r.stage} />
                    <span className="text-xs text-neutral-500">
                      Updated {formatEnrollmentDate(r.stage_changed_at)}
                    </span>
                  </div>
                </div>
                <CaretRight
                  size={18}
                  weight="bold"
                  className="mt-1 flex-shrink-0 text-neutral-400"
                />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
